import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Checkbox, FormInput, PhoneInput, Select } from '@/components';
import { DetailInfo } from '@/screens/profile/_components/profile-card/profile-card.types';
import { QUERY_KEYS } from '@/constants/QueryKeys';
import { useQueryClient } from 'react-query';
import { useMemo } from 'react';
import { useApi } from '@/api/useApi';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { WORK_STATUS } from '@/screens/application-onboarding/_components/onboarding-form/steps/work-info/work-info.constants.ts';
import { UserType } from '@/constants';
import clsx from 'clsx';
import { BulkExpertBranchCreateRequest, ExpertBranchCreateRequestItem, ProfileInfoUpdateRequest } from '@dopingtech/live_expert_bff_api_ts';
import NotificationManager from '@/managers/notification-manager.tsx';
import { ProfileEditFormProps, District, City, FormValues } from './profile-edit-moda.types.ts';
import { schema } from './profile-edit-modal.schema.ts';
import { useNavigate } from 'react-router';
import { NavigationPath } from '@/router/navigation-path.ts';

export const ProfileEditForm = ({ userType, formConfig, closeModal }: ProfileEditFormProps) => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { LookUpControllerApi, EducationLevelControllerApiExpert, BranchControllerApiExpert, ExpertControllerApi, ExpertBranchControllerApi } = useApi();
	const detailInfo = queryClient.getQueryData<{ data: { data: DetailInfo } }>(QUERY_KEYS.getLoggedInExpertDetailInfo)?.data?.data;

	const defaultValues = {
		phone: detailInfo?.expert?.phone || '',
		name: detailInfo?.expert?.name || '',
		surname: detailInfo?.expert?.surname || '',
		email: detailInfo?.expert?.email || '',
		city: detailInfo?.expert?.cityCode || '',
		district: detailInfo?.expert?.districtCode || '',
		workStatus: detailInfo?.expert?.schoolType || '',
		levels: detailInfo?.educationLevelList?.map(level => level.name) || [],
		...detailInfo?.educationLevelList?.reduce(
			(acc, level) => ({
				...acc,
				[`subjects_${level.name}`]: detailInfo?.branchList?.filter(branch => branch.branchName.startsWith(level.name))?.map(branch => branch.lessonId.toString()) || []
			}),
			{}
		)
	};

	const {
		handleSubmit,
		control,
		watch,
		setValue,
		unregister,
		formState: { errors, isValid, isDirty }
	} = useForm<FormValues>({
		resolver: yupResolver(schema),
		mode: 'onChange',
		defaultValues,
		context: { userType },
		shouldUnregister: true
	});

	const { data: citiesResponse } = useQueryProvider([QUERY_KEYS.getAllCities], () => LookUpControllerApi.getAllCities(), {
		select: response => response?.data ?? [],
		refetchOnWindowFocus: false
	});

	const { data: districtsResponse } = useQueryProvider([QUERY_KEYS.getAllDistricts, watch('city')], () => (watch('city') ? LookUpControllerApi.getAllDistricts(watch('city')) : null), {
		select: response => response?.data ?? [],
		refetchOnWindowFocus: false,
		enabled: !!watch('city')
	});

	const { data: educationLevels } = useQueryProvider([QUERY_KEYS.getEducationLevels], () => EducationLevelControllerApiExpert.getEducationLevels(), {
		select: ({ data }) => data?.data ?? [],
		refetchOnWindowFocus: false
	});

	const { data: branchesData } = useQueryProvider(
		[QUERY_KEYS.getBranchesByEducationLevel, watch('levels')],
		async () => {
			const selectedLevels = watch('levels') || [];
			if (!selectedLevels.length) return [];

			if (userType === UserType.UNIVERSITY_STUDENT) {
				const levelId = educationLevels?.find(l => l.name === selectedLevels[0])?.id;
				const res = await BranchControllerApiExpert.getBranchesByEducationLevel(levelId);
				return res?.data?.data || [];
			}

			const promises = selectedLevels.map(level => {
				const levelId = educationLevels?.find(l => l.name === level)?.id;
				return levelId ? BranchControllerApiExpert.getBranchesByEducationLevel(levelId) : null;
			});

			const responses = await Promise.all(promises.filter(Boolean));
			return responses.reduce((acc, response) => {
				if (response?.data?.data) {
					acc.push(...response.data.data);
				}
				return acc;
			}, []);
		},
		{
			select: data => data ?? [],
			enabled: !!watch('levels')?.length && !!educationLevels?.length && userType !== UserType.COACH,
			refetchOnWindowFocus: false
		}
	);

	const updateProfileInfo = useMutationProvider((body: ProfileInfoUpdateRequest) => ExpertControllerApi.updateProfileInfo(body));
	const updateExpertBranches = useMutationProvider((body: BulkExpertBranchCreateRequest) => ExpertBranchControllerApi.createExpertBranches(body));

	const isSubmitDisabled = !formConfig.submitForm || !isDirty || !isValid || updateProfileInfo.isLoading || updateExpertBranches.isLoading;

	const branchOptionsByLevel = useMemo(() => {
		const prevOptions = queryClient.getQueryData([QUERY_KEYS.branchOptionsCache]) || {};

		if (!branchesData?.length) return prevOptions;

		const options = {};

		branchesData.forEach(branch => {
			const levelName = branch?.branchName?.split(' ')[0];
			if (!options[levelName]) {
				options[levelName] = [];
			}
			options[levelName].push({
				value: branch?.lessonId?.toString(),
				label: branch.lessonName,
				branchId: branch.branchId
			});
		});

		queryClient.setQueryData([QUERY_KEYS.branchOptionsCache], options);

		return options;
	}, [branchesData, queryClient]);

	const cityOptions = useMemo(() => {
		if (!Array.isArray(citiesResponse?.data)) return [];
		return citiesResponse?.data.map((city: City) => ({
			value: city.id.toString(),
			label: city.value
		}));
	}, [citiesResponse]);

	const districtOptions = useMemo(() => {
		if (!Array.isArray(districtsResponse?.data)) return [];
		return districtsResponse?.data.map((district: District) => ({
			value: district.id.toString(),
			label: district.value
		}));
	}, [districtsResponse]);

	const onClickDeleteAccount = () => {
		navigate(NavigationPath.deleteAccount);
	};

	const renderCommonFields = () => (
		<>
			<div className="form-row">
				<div className="input-half-width">
					<Controller
						name="name"
						control={control}
						render={({ field }) => (
							<FormInput
								label="Ad"
								{...field}
								disabled={!formConfig.name}
								error={errors.name?.message}
							/>
						)}
					/>
				</div>
				<div className="input-half-width">
					<Controller
						name="surname"
						control={control}
						render={({ field }) => (
							<FormInput
								label="Soyad"
								{...field}
								disabled={!formConfig.surname}
								error={errors.surname?.message}
							/>
						)}
					/>
				</div>
			</div>

			<div className="form-row">
				<div className="input-half-width">
					<Controller
						name="city"
						control={control}
						render={({ field }) => (
							<Select
								label="İl"
								options={cityOptions}
								{...field}
								disabled={!formConfig.city}
								error={errors.city?.message}
								placeholder="İl seçin"
							/>
						)}
					/>
				</div>
				<div className="input-half-width">
					<Controller
						name="district"
						control={control}
						render={({ field }) => (
							<Select
								label="İlçe"
								options={districtOptions}
								{...field}
								disabled={!formConfig.district || !watch('city')}
								error={errors.district?.message}
								placeholder="İlçe seçin"
							/>
						)}
					/>
				</div>
			</div>

			<div className="form-row">
				<div className="input-full-width">
					<Controller
						name="phone"
						control={control}
						render={({ field: { onChange, value } }) => (
							<PhoneInput
								value={value}
								onChange={onChange}
								disabled={!formConfig.phone}
							/>
						)}
					/>
				</div>
			</div>

			<div className="form-row">
				<div className="input-full-width">
					<Controller
						name="email"
						control={control}
						render={({ field }) => (
							<FormInput
								label="E-Posta"
								{...field}
								disabled={!formConfig.email}
								error={errors.email?.message}
							/>
						)}
					/>
				</div>
			</div>
		</>
	);

	const renderCoachForm = () => (
		<>
			{renderCommonFields()}

			<div className="form-sections-wrapper">
				<div className="form-section">
					<h3>Çalışma Durumu</h3>
					<Controller
						name="workStatus"
						control={control}
						render={({ field: { onChange, value } }) => (
							<div className="checkbox-group">
								{WORK_STATUS.map(option => (
									<Checkbox
										key={option.value}
										label={option.label}
										variant="pill"
										checked={value === option.value}
										onChange={() => onChange(option.value)}
										disabled={!formConfig.workStatus}
									/>
								))}
							</div>
						)}
					/>
				</div>

				<div className="form-section">
					<h3>Kademeler</h3>
					<Controller
						name="levels"
						control={control}
						defaultValue={[]}
						render={({ field: { onChange, value = [] } }) => (
							<div className="checkbox-group">
								{educationLevels?.map(option => (
									<Checkbox
										key={option.id}
										label={option.name}
										variant="pill"
										checked={value.includes(option.name)}
										onChange={() => {
											const newValue = value.includes(option.name) ? value.filter(v => v !== option.name) : [...value, option.name];
											if (newValue.length > 0) {
												onChange(newValue);
											} else if (!value.includes(option.name)) {
												onChange(newValue);
											}
										}}
										disabled={!formConfig.levels}
									/>
								))}
							</div>
						)}
					/>
				</div>
			</div>
		</>
	);

	const renderUniversityStudentForm = () => (
		<>
			{renderCommonFields()}

			<div className="form-row">
				<div className="input-full-width">
					<Controller
						name="levels"
						control={control}
						render={({ field: { onChange, value } }) => (
							<Select
								label="Kademe"
								options={educationLevels?.map(level => ({
									value: level.id,
									label: level.name
								}))}
								value={Array.isArray(value) ? value[0] : value}
								onChange={e => {
									const selectedId = e.target.value;
									const selectedLevel = educationLevels?.find(level => level?.id?.toString() === selectedId);
									const newValue = selectedLevel ? [selectedLevel.name] : [];
									onChange(newValue);

									if (value?.[0]) {
										setValue(`subjects_${value[0]}`, []);
										unregister(`subjects_${value[0]}`);
									}

									if (selectedLevel?.name) {
										setValue(`subjects_${selectedLevel.name}`, []);
									}
								}}
								disabled={!formConfig.universityLevel}
								error={errors.levels?.message}
								placeholder="Kademe seçin"
							/>
						)}
					/>
				</div>
			</div>

			<div className="form-row">
				<div className="input-full-width">
					<Controller
						name={`subjects_${watch('levels')}`}
						control={control}
						render={({ field: { onChange, value } }) => (
							<Select
								variant="multiple"
								title={
									<div className="subject-multiple-select-title">
										<i className="icons-info-circle" />
										<p>
											En fazla <b>3 ders</b> seçebilirsiniz.
										</p>
									</div>
								}
								options={branchOptionsByLevel[watch('levels')] || []}
								selectedValues={value || []}
								onMultipleChange={newValues => {
									if (newValues.length <= 3) {
										onChange(newValues);
									}
								}}
								maxSelection={3}
								error={errors[`subjects_${watch('levels')}`]?.message || errors.levels?.message}
								placeholder="Ders seçin"
								disabled={!formConfig.department}
								hiddenMultipleSaveButton
							/>
						)}
					/>
				</div>
			</div>
		</>
	);

	const renderInstructorForm = () => (
		<>
			<div className="form-row-wrapper">
				<div className="form-row">
					<div className="input-half-width">
						<Controller
							name="name"
							control={control}
							render={({ field }) => (
								<FormInput
									label="Ad"
									{...field}
									disabled={!formConfig.name}
									error={errors.name?.message}
								/>
							)}
						/>
					</div>
					<div className="input-half-width">
						<Controller
							name="surname"
							control={control}
							render={({ field }) => (
								<FormInput
									label="Soyad"
									{...field}
									disabled={!formConfig.surname}
									error={errors.surname?.message}
								/>
							)}
						/>
					</div>
				</div>

				<div className="form-row">
					<div className="input-half-width">
						<Controller
							name="city"
							control={control}
							render={({ field }) => (
								<Select
									label="İl"
									options={cityOptions}
									{...field}
									disabled={!formConfig.city}
									error={errors.city?.message}
									placeholder="İl seçin"
								/>
							)}
						/>
					</div>
					<div className="input-half-width">
						<Controller
							name="district"
							control={control}
							render={({ field }) => (
								<Select
									label="İlçe"
									options={districtOptions}
									{...field}
									disabled={!formConfig.district || !watch('city')}
									error={errors.district?.message}
									placeholder="İlçe seçin"
								/>
							)}
						/>
					</div>
				</div>
			</div>

			<div className="form-row-wrapper">
				<div className="form-row">
					<div className="input-full-width">
						<Controller
							name="phone"
							control={control}
							render={({ field: { onChange, value } }) => (
								<PhoneInput
									value={value}
									onChange={onChange}
									disabled={!formConfig.phone}
								/>
							)}
						/>
					</div>
				</div>

				<div className="form-row">
					<div className="input-full-width">
						<Controller
							name="email"
							control={control}
							render={({ field }) => (
								<FormInput
									label="E-Posta"
									{...field}
									disabled={!formConfig.email}
									error={errors.email?.message}
								/>
							)}
						/>
					</div>
				</div>
			</div>

			<div className="form-sections-wrapper">
				<div className="form-section">
					<h3>Çalışma Durumu</h3>
					<Controller
						name="workStatus"
						control={control}
						render={({ field: { onChange, value } }) => (
							<div className="checkbox-group-flex">
								{WORK_STATUS.map(option => (
									<Checkbox
										key={option.value}
										label={option.label}
										variant="pill"
										checked={value === option.value}
										onChange={() => onChange(option.value)}
										disabled={!formConfig.workStatus}
									/>
								))}
							</div>
						)}
					/>
				</div>

				<div className="form-section">
					<h3>Kademeler</h3>
					<div className="profile-edit__education-grid">
						<Controller
							name="levels"
							control={control}
							defaultValue={[]}
							render={({ field: { onChange, value = [] } }) => (
								<>
									{educationLevels?.map(level => {
										const levelName = level.name;
										const isSelected = value.includes(levelName);
										return (
											<div
												key={`${levelName}-${level.id}`}
												className={clsx('profile-edit__education-card', {
													'profile-edit__education-card--selected': isSelected
												})}>
												<Checkbox
													label={levelName}
													checked={isSelected}
													onChange={() => {
														if (isSelected) {
															if (value.length <= 1) {
																return;
															}

															unregister(`subjects_${levelName}`);
															const newValue = value.filter(v => v !== levelName);
															onChange(newValue);
															setValue(`subjects_${levelName}`, []);
														} else {
															const newValue = [...value, levelName];
															onChange(newValue);
														}
													}}
													variant="pill"
													disabled={!formConfig.levels}
												/>

												<div className="profile-edit__education-card-select">
													<Controller
														name={`subjects_${levelName}`}
														control={control}
														defaultValue={[]}
														shouldUnregister
														render={({ field: { onChange: onChangeSubjects, value: selectedSubjects = [] } }) => (
															<>
																<Select
																	variant="multiple"
																	options={branchOptionsByLevel[levelName] || []}
																	selectedValues={selectedSubjects}
																	onMultipleChange={newValues => {
																		if (newValues.length <= 3) {
																			onChangeSubjects(newValues);
																			if (newValues.length === 0) {
																				const newLevels = value.filter(v => v !== levelName);
																				if (newLevels.length > 0) {
																					onChange(newLevels);
																					unregister(`subjects_${levelName}`);
																				}
																			}
																		}
																	}}
																	maxSelection={3}
																	placeholder="Ders Seçimi"
																	alwaysShowPlaceholder
																	disabled={!formConfig.levels || !isSelected}
																	hiddenMultipleSaveButton
																/>

																{selectedSubjects?.length > 0 && (
																	<div className="profile-edit__education-card-courses">
																		<div className="profile-edit__education-card-courses-title">Seçilen Dersler</div>
																		<div className="profile-edit__education-card-courses-list">
																			{selectedSubjects.map(subjectId => {
																				const subject = branchOptionsByLevel[levelName]?.find(opt => opt.value === subjectId);
																				return (
																					subject && (
																						<div
																							key={subjectId}
																							className="profile-edit__education-card-courses-pill">
																							{subject.label}
																							<button
																								type="button"
																								className="profile-edit__education-card-courses-pill-remove"
																								onClick={e => {
																									e.preventDefault();
																									e.stopPropagation();
																									const newValues = selectedSubjects.filter(id => id !== subjectId);
																									onChangeSubjects(newValues);
																									if (newValues.length === 0) {
																										const newLevels = value.filter(v => v !== levelName);
																										if (newLevels.length > 0) {
																											onChange(newLevels);
																											unregister(`subjects_${levelName}`);
																										}
																									}
																								}}>
																								<i className="icons-close-circle" />
																							</button>
																						</div>
																					)
																				);
																			})}
																		</div>
																	</div>
																)}
															</>
														)}
													/>
												</div>
											</div>
										);
									})}
								</>
							)}
						/>
					</div>
				</div>
			</div>
		</>
	);

	const onFormSubmit = handleSubmit(async data => {
		const selectedCity = cityOptions?.find(city => city.value === data.city);
		const selectedDistrict = districtOptions?.find(district => district.value === data.district);

		const profileInfo: ProfileInfoUpdateRequest = {
			name: data.name,
			surname: data.surname,
			schoolType: data.workStatus,
			email: data.email,
			cityName: selectedCity?.label,
			cityCode: data.city,
			districtName: selectedDistrict?.label,
			districtCode: data.district
		};

		const branches: ExpertBranchCreateRequestItem[] = [];

		data.levels.forEach(levelName => {
			const educationLevel = educationLevels?.find(level => level.name === levelName);
			const selectedSubjects = data[`subjects_${levelName}`] || [];

			if (UserType.COACH === userType) {
				return branches.push({
					educationLevelId: educationLevel?.id
				});
			}

			selectedSubjects.forEach(subjectId => {
				const branch = branchesData?.find(b => b.lessonId.toString() === subjectId && b.branchName.startsWith(levelName));

				if (educationLevel?.id && branch?.branchId) {
					branches.push({
						branchId: branch.branchId,
						educationLevelId: educationLevel.id
					});
				}
			});
		});

		const expertBranches: BulkExpertBranchCreateRequest = {
			branches: branches
		};

		await updateProfileInfo.mutateAsync(profileInfo);
		await updateExpertBranches.mutateAsync(expertBranches);
		queryClient.refetchQueries(QUERY_KEYS.getLoggedInExpertDetailInfo);
		closeModal();
		NotificationManager.showMessage('success', 'Profil bilgileriniz başarıyla güncellendi.');
	});

	return (
		<form
			onSubmit={onFormSubmit}
			className="profile-edit-form-container">
			<div className="profile-edit-form">
				<div className="form-header">
					<h2>Profil Düzenle</h2>
					<p>Profil bilgilerinizi eksiksiz şekilde doldurunuz.</p>
				</div>

				{userType === UserType.COACH && renderCoachForm()}
				{userType === UserType.UNIVERSITY_STUDENT && renderUniversityStudentForm()}
				{userType === UserType.INSTRUCTOR && renderInstructorForm()}
			</div>

			<div className="button-wrapper">
				<Button
					type="submit"
					disabled={isSubmitDisabled}
					fullWidth>
					Değişiklikleri Kaydet
				</Button>
				<div
					className="delete-button"
					onClick={onClickDeleteAccount}>
					<span className="button-content">
						<i className="icons-trash-bin-minimalistic" /> Hesabımı Sil
					</span>
				</div>
			</div>
		</form>
	);
};
